import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Copy, Check } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Input } from '@/components/ui';
import type { VendorCredentialIssueResult } from '../types/vendorCredential';
import {
  vendorCredentialIssueSchema,
  type VendorCredentialIssueValues,
} from '../schemas/vendorReviewSchema';

export interface VendorCredentialIssueModalProps {
  isOpen: boolean;
  applicationName: string;
  defaultEmail?: string;
  defaultName?: string;
  isSubmitting: boolean;
  errorMessage?: string | null;
  result?: VendorCredentialIssueResult | null;
  /** 재발급(reset) 모드 — 타이틀/버튼 라벨을 "재발급"으로 표시. 폼 입력은 없고 결과만. */
  isReset?: boolean;
  onSubmit: (values: VendorCredentialIssueValues) => void;
  onClose: () => void;
}

/** 발급 결과 1행(라벨 + monospace 값 + 복사 버튼). 평문 자격증명 전달용. */
const CredentialRow = ({ label, value }: { label: string; value: string }) => {
  const { t } = useTranslation('vendors');
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // 클립보드 거부 시 무음 — 값은 화면에 그대로 노출돼 수동 복사 가능.
    }
  };
  return (
    <div className="flex items-center gap-2">
      <span className="w-24 shrink-0 text-xs text-[color:var(--content-muted)]">{label}</span>
      <code
        className="min-w-0 flex-1 truncate rounded px-2 py-1 font-mono text-sm"
        style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)', color: 'var(--content-strong)' }}
        title={value}
      >
        {value}
      </code>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => void copy()}
        aria-label={t('vendors.credential.copy', { value: label, defaultValue: '복사' })}
      >
        {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
      </Button>
    </div>
  );
};

export const VendorCredentialIssueModal = ({
  isOpen,
  applicationName,
  defaultEmail,
  defaultName,
  isSubmitting,
  errorMessage,
  result,
  isReset = false,
  onSubmit,
  onClose,
}: VendorCredentialIssueModalProps) => {
  const { t } = useTranslation('vendors');
  const form = useForm<VendorCredentialIssueValues>({
    resolver: zodResolver(vendorCredentialIssueSchema),
    defaultValues: {
      initialAdminEmail: defaultEmail ?? '',
      initialAdminName: defaultName ?? '',
    },
    mode: 'onBlur',
  });
  const emailRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      form.reset({
        initialAdminEmail: defaultEmail ?? '',
        initialAdminName: defaultName ?? '',
      });
      requestAnimationFrame(() => emailRef.current?.focus());
    }
  }, [isOpen, defaultEmail, defaultName, form]);

  const { ref: emailRegisterRef, ...emailRegister } = form.register('initialAdminEmail');
  const emailError = form.formState.errors.initialAdminEmail?.message;
  const nameError = form.formState.errors.initialAdminName?.message;
  const showResult = Boolean(result);

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o && !isSubmitting) onClose(); }}>
      <DialogContent data-testid="vendor-credential-issue-modal" className="max-w-md">
        <DialogTitle>
          {isReset ? t('vendors.actions.resetCredentials') : t('vendors.actions.issueCredentials')}
        </DialogTitle>
        <DialogDescription>
          {t('vendors.actionModal.subject', { vendorName: applicationName })}
        </DialogDescription>

        {showResult && result ? (
          <div
            role="status"
            aria-live="polite"
            data-testid="vendor-credential-result"
            className="mt-4 space-y-3 rounded-md p-3"
            style={{
              background: 'var(--status-success-bg)',
              color: 'var(--status-success-fg)',
            }}
          >
            {/* G-1(9차): 평문 loginId+oneTimePassword 는 이 응답에서 1회만 노출 → 복사 + 경고. */}
            <div
              role="alert"
              className="rounded-md px-2 py-1.5 text-xs font-medium"
              style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
            >
              {t(
                'vendors.credential.oneTimeWarning',
                '비밀번호는 지금만 확인할 수 있습니다. 닫기 전에 복사하여 소속사에 전달하세요. 분실 시 [재발급]이 필요합니다.',
              )}
            </div>
            <div className="space-y-2">
              <CredentialRow label={t('vendors.credential.loginId', '로그인 ID')} value={result.loginId} />
              <CredentialRow
                label={t('vendors.credential.oneTimePassword', '1회용 비밀번호')}
                value={result.oneTimePassword}
              />
            </div>
            <dl className="grid grid-cols-3 gap-1 text-xs">
              <dt>{t('vendors.credential.vendorId')}</dt>
              <dd className="col-span-2 font-mono">{result.vendorId}</dd>
              <dt>{t('vendors.credential.email')}</dt>
              <dd className="col-span-2">{result.initialAdminEmail}</dd>
            </dl>
            {result.nextSteps ? <p className="text-xs">{result.nextSteps}</p> : null}
            <div className="flex justify-end">
              <Button type="button" variant="cta" size="sm" onClick={onClose}>
                {t('vendors.actions.close')}
              </Button>
            </div>
          </div>
        ) : isReset ? (
          // 재발급은 입력 폼 없이 확인만 — 기존 loginId 유지, 새 1회용 비번 발급.
          <div className="mt-4 space-y-4">
            <p className="text-sm text-[color:var(--content-default)]">
              {t(
                'vendors.credential.resetConfirm',
                '기존 로그인 ID 는 유지되고 새 1회용 비밀번호가 발급됩니다. 계속하시겠습니까?',
              )}
            </p>
            {errorMessage ? (
              <p
                role="alert"
                aria-live="polite"
                className="rounded-md p-2 text-sm"
                style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
              >
                {errorMessage}
              </p>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" size="md" onClick={onClose} disabled={isSubmitting}>
                {t('vendors.actions.cancel')}
              </Button>
              <Button
                type="button"
                variant="cta"
                size="md"
                disabled={isSubmitting}
                onClick={() =>
                  onSubmit({ initialAdminEmail: defaultEmail ?? '', initialAdminName: defaultName ?? '' })
                }
              >
                {isSubmitting ? t('vendors.actionModal.submitting') : t('vendors.actions.resetCredentialsConfirm')}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 space-y-4" noValidate>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="vendor-credential-email"
                className="text-sm text-[color:var(--content-muted)]"
              >
                {t('vendors.credential.initialAdminEmail')}
                <span aria-hidden="true" className="ml-1 text-[color:var(--signal-danger)]">
                  *
                </span>
              </label>
              <Input
                id="vendor-credential-email"
                type="email"
                {...emailRegister}
                ref={(el) => {
                  emailRegisterRef(el);
                  emailRef.current = el;
                }}
                required
                aria-required="true"
                aria-invalid={Boolean(emailError)}
                aria-describedby={emailError ? 'vendor-credential-email-error' : undefined}
                maxLength={254}
                style={{ height: 'var(--density-compact-form-h)' }}
              />
              {emailError ? (
                <span
                  id="vendor-credential-email-error"
                  role="alert"
                  className="text-xs"
                  style={{ color: 'var(--signal-danger)' }}
                >
                  {t(emailError, { defaultValue: emailError })}
                </span>
              ) : null}
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="vendor-credential-name"
                className="text-sm text-[color:var(--content-muted)]"
              >
                {t('vendors.credential.initialAdminName')}
                <span aria-hidden="true" className="ml-1 text-[color:var(--signal-danger)]">
                  *
                </span>
              </label>
              <Input
                id="vendor-credential-name"
                {...form.register('initialAdminName')}
                required
                aria-required="true"
                aria-invalid={Boolean(nameError)}
                aria-describedby={nameError ? 'vendor-credential-name-error' : undefined}
                maxLength={100}
                style={{ height: 'var(--density-compact-form-h)' }}
              />
              {nameError ? (
                <span
                  id="vendor-credential-name-error"
                  role="alert"
                  className="text-xs"
                  style={{ color: 'var(--signal-danger)' }}
                >
                  {t(nameError, { defaultValue: nameError })}
                </span>
              ) : null}
            </div>

            <p className="text-xs text-[color:var(--content-muted)]">
              {t('vendors.credential.securityNote')}
            </p>

            {errorMessage ? (
              <p
                role="alert"
                aria-live="polite"
                className="rounded-md p-2 text-sm"
                style={{
                  background: 'var(--status-danger-bg)',
                  color: 'var(--status-danger-fg)',
                }}
              >
                {errorMessage}
              </p>
            ) : null}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {t('vendors.actions.cancel')}
              </Button>
              <Button type="submit" variant="cta" size="md" disabled={isSubmitting}>
                {isSubmitting
                  ? t('vendors.actionModal.submitting')
                  : t('vendors.actions.issueCredentialsConfirm')}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
